import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Flex, HStack, Button, Text, Icon, IconButton, VStack, Collapse, useDisclosure, Image,
    Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import { FiMenu, FiX, FiMapPin, FiChevronDown } from 'react-icons/fi';

const SPECIALTY_LABELS = new Set(['Wash & Fold', 'Ozone Wash', 'Comforters', 'Saree Rolling']);

function NavDropdown({ label, links, linkHref, themeColor, emphasize }) {
    return (
        <Menu>
            <MenuButton as={Box} cursor="pointer" fontSize="sm" fontWeight={emphasize ? '700' : '500'}
                color={emphasize ? `${themeColor}.600` : 'gray.600'} whiteSpace="nowrap"
                _hover={{ color: `${themeColor}.500` }} transition="color 0.2s">
                <HStack spacing={1}>
                    <Text>{label}</Text>
                    <Icon as={FiChevronDown} boxSize={3} />
                </HStack>
            </MenuButton>
            <MenuList minW="180px">
                {links.map((link) => (
                    <MenuItem key={link.label} as="a" href={linkHref(link)} fontSize="sm"
                        fontWeight={link.isMembership ? '700' : '500'}
                        color={(link.isAccount || link.isMembership || link.isCommercial) ? `${themeColor}.600` : 'gray.700'}>
                        {link.menuLabel || link.label}
                    </MenuItem>
                ))}
            </MenuList>
        </Menu>
    );
}

const baseNavLinks = [
    { label: 'Services', href: '#services' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Location', href: '#location' },
    { label: 'About', href: '#about' },
    { label: 'My Account', href: null, isAccount: true },
    { label: 'Commercial', href: null, isCommercial: true },
    { label: 'WDF POS', href: null, isAdmin: true },
    { label: 'Driver', href: null, isDriver: true },
];

export default function SiteNavbar({ config }) {
    const { isOpen, onToggle } = useDisclosure();
    const sc = config?.siteContent || {};
    const laundryId = config?.laundryId || '1';
    const laundryName = config?.laundryName || 'Laundry';
    const themeColor = sc.themeColor || 'blue';
    // Some tenants save the full "street, city, ST zip" in address; avoid repeating the city.
    const displayAddress = sc.address
        ? (sc.city && sc.address.toLowerCase().includes(sc.city.toLowerCase())
            ? sc.address
            : [sc.address, sc.city, sc.state].filter(Boolean).join(', '))
        : '';

    // Show a "Membership" nav link only when this tenant actually offers plans.
    // The public landing page is unauthenticated, so fetch plans directly from
    // the public endpoint (works without a token). The link points at the
    // membership-first journey (/:laundryId/user/join), which is auth-gated and
    // routes visitors through login first.
    const [hasMembershipPlans, setHasMembershipPlans] = useState(false);
    useEffect(() => {
        if (!laundryId) return;
        const API = process.env.REACT_APP_AWS_API_URL;
        axios.get(`${API}/api/membership/plans`, { params: { laundryId } })
            .then((res) => setHasMembershipPlans((res.data?.plans || []).length > 0))
            .catch(() => {});
    }, [laundryId]);

    // Build nav links dynamically — only show Saree Rolling if tenant has it
    const services = config?.services || [];
    const serviceCategories = config?.serviceCategories || [];
    const hasSaree = services.some(s => s.serviceName?.toLowerCase().includes('saree')) ||
                     serviceCategories.some(c => c.categoryName?.toLowerCase().includes('saree'));
    let navLinks = hasSaree
        ? [...baseNavLinks.slice(0, 3), { label: 'Saree Rolling', href: `/${laundryId}/saree-rolling` }, ...baseNavLinks.slice(3)]
        : baseNavLinks;
    if (String(laundryId) === '1') {
        navLinks = [
            ...navLinks.slice(0, 3),
            { label: 'Wash & Fold', href: '/1/wash-and-fold' },
            { label: 'Ozone Wash', href: '/ozone-wash' },
            { label: 'Comforters', href: '/1/comforter-cleaning' },
            ...navLinks.slice(3),
        ];
    }
    const showMembershipStory = String(laundryId) === '1' || String(laundryId) === '2';
    if (showMembershipStory && !navLinks.some((l) => l.href === '#membership')) {
        const pricingIdx = navLinks.findIndex((l) => l.href === '#pricing');
        const plansLink = { label: 'Membership', href: '#membership' };
        navLinks = pricingIdx >= 0
            ? [...navLinks.slice(0, pricingIdx), plansLink, ...navLinks.slice(pricingIdx)]
            : [...navLinks, plansLink];
    }

    // Per-tenant nav visibility flags (site_content). Default = show, so
    // existing tenants are unchanged until they opt in from the admin panel.
    if (sc.hideNavServices) {
        navLinks = navLinks.filter((l) => l.href !== '#services');
    }
    if (sc.hideNavStaffLinks) {
        // Hide Admin + Driver shortcuts from the public navbar. Staff still
        // reach them via direct URL (/:laundryId/admin, /:laundryId/driver/home).
        navLinks = navLinks.filter((l) => !l.isAdmin && !l.isDriver);
    }
    // Keep the navbar in sync with which SECTIONS are actually rendered
    // (SiteLandingPage gates each section on these same hide* flags). Without
    // this, disabling a section left its nav link pointing at a missing anchor.
    if (sc.hideHowItWorks) {
        navLinks = navLinks.filter((l) => l.href !== '#how-it-works');
    }
    if (sc.hidePricing) {
        navLinks = navLinks.filter((l) => l.href !== '#pricing');
    }
    if (sc.hideLocation) {
        navLinks = navLinks.filter((l) => l.href !== '#location');
    }
    if (sc.hideAbout) {
        navLinks = navLinks.filter((l) => l.href !== '#about');
    }
    // Recurring section only when this shop offers repeat intervals.
    const frequencies = Array.isArray(config?.frequencyInterval) ? config.frequencyInterval : [];
    if (frequencies.length > 0 && !sc.hideRecurring) {
        const recurringLink = { label: 'Recurring', href: '#recurring' };
        const howIdx = navLinks.findIndex((l) => l.href === '#how-it-works');
        const pricingIdx = navLinks.findIndex((l) => l.href === '#pricing');
        const insertAt = howIdx >= 0 ? howIdx + 1 : (pricingIdx >= 0 ? pricingIdx : navLinks.length);
        navLinks = [...navLinks.slice(0, insertAt), recurringLink, ...navLinks.slice(insertAt)];
    }
    // Public FAQ index (/:laundryId/faq). Placed with the content links so it
    // stays in the bar instead of folding into the Account menu.
    const faqLink = { label: 'FAQ', href: `/${laundryId}/faq` };
    const faqInsertAt = navLinks.findIndex((l) => l.isAccount);
    if (faqInsertAt >= 0) {
        navLinks = [...navLinks.slice(0, faqInsertAt), faqLink, ...navLinks.slice(faqInsertAt)];
    } else {
        navLinks = [...navLinks, faqLink];
    }
    // Insert the Membership link (before the account/staff links) when the
    // tenant offers plans. Routed to the membership-first journey.
    if (hasMembershipPlans) {
        const accountIdx = navLinks.findIndex((l) => l.isAccount);
        const memberLink = {
            label: showMembershipStory ? 'Join' : 'Membership',
            href: null,
            isMembership: true,
        };
        if (accountIdx >= 0) {
            navLinks = [...navLinks.slice(0, accountIdx), memberLink, ...navLinks.slice(accountIdx)];
        } else {
            navLinks = [...navLinks, memberLink];
        }
    }

    // Resolve a nav link to its destination URL (shared by desktop + mobile).
    const linkHref = (link) =>
        link.isAdmin ? `/${laundryId}/admin`
        : link.isDriver ? `/${laundryId}/driver/home`
        : link.isAccount ? `/${laundryId}/login`
        : link.isMembership ? `/${laundryId}/user/join`
        : link.isCommercial ? `/${laundryId}/commercial`
        : link.href;

    // Desktop row groups specialty pages and account links into menus so
    // "Schedule Pickup" stays in view. Mobile keeps the flat list.
    const customerLinks = navLinks.filter((l) => !l.isAdmin && !l.isDriver);
    const isAccountLink = (l) => l.isAccount || l.isMembership || l.isCommercial;
    const specialtyLinks = customerLinks.filter((l) => SPECIALTY_LABELS.has(l.label));
    const servicesAnchor = customerLinks.find((l) => l.href === '#services');
    const serviceMenuLinks = [
        ...(servicesAnchor && specialtyLinks.length
            ? [{ ...servicesAnchor, menuLabel: 'All Services' }]
            : servicesAnchor ? [servicesAnchor] : []),
        ...specialtyLinks,
    ];
    const useServicesMenu = serviceMenuLinks.length >= 2;
    const accountLinks = customerLinks.filter(isAccountLink);
    const useAccountMenu = accountLinks.length >= 2;
    const inlineLinks = customerLinks.filter((l) => {
        if (useServicesMenu && (l.href === '#services' || SPECIALTY_LABELS.has(l.label))) return false;
        if (useAccountMenu && isAccountLink(l)) return false;
        return true;
    });

    return (
        <Box as="nav" position="sticky" top="0" zIndex="1000" bg="white" borderBottom="1px solid" borderColor="gray.100" boxShadow="sm">
            <Flex w="100%" maxW="1200px" minW={0} mx="auto" px={{ base: 4, md: 6 }} py={3} align="center" justify="space-between" gap={3}>
                <HStack spacing={3} flexShrink={1} minW={0} mr={2}>
                    {config?.laundryLogo && (
                        <Image src={config.laundryLogo} alt={laundryName} h={{ base: '48px', md: '64px' }} maxW={{ base: '150px', md: '200px' }} objectFit="contain" borderRadius="md" />
                    )}
                    <VStack spacing={0} align="flex-start" minW={0}>
                        <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="800" color={`${themeColor}.600`} whiteSpace="nowrap" noOfLines={1} minW={0} title={laundryName}>
                            {laundryName}
                        </Text>
                        <HStack spacing={1} maxW={{ base: '200px', md: '260px' }} minW={0}>
                            <Icon as={FiMapPin} color="gray.400" boxSize={3} flexShrink={0} />
                            {sc.address ? (
                                <Text as="a" href={`https://www.google.com/maps/dir/?api=1&destination=${sc.mapsQuery || ''}`}
                                    target="_blank" rel="noopener noreferrer" fontSize="xs" color="gray.400" _hover={{ color: `${themeColor}.500` }}
                                    noOfLines={1} title={displayAddress}>
                                    {displayAddress}
                                </Text>
                            ) : (
                                <Text fontSize="xs" color="gray.400" noOfLines={1}>
                                    {[sc.city, sc.state].filter(Boolean).join(', ')}
                                </Text>
                            )}
                        </HStack>
                    </VStack>
                </HStack>

                <HStack spacing={4} display={{ base: 'none', xl: 'flex' }} flexShrink={0} flexWrap="nowrap">
                    {/* Specialty pages (Wash & Fold, Comforters, Saree Rolling) fold
                        into Services. Account links fold into Account. Staff stays
                        its own menu. That keeps Schedule Pickup on screen. */}
                    {useServicesMenu && (
                        <NavDropdown label="Services" links={serviceMenuLinks} linkHref={linkHref} themeColor={themeColor} />
                    )}
                    {inlineLinks.map((link) => (
                        <Box key={link.label} as="a"
                            href={linkHref(link)}
                            fontSize="sm" fontWeight={link.isMembership ? '700' : '500'}
                            whiteSpace="nowrap"
                            color={(link.isAccount || link.isMembership || link.isCommercial) ? `${themeColor}.600` : 'gray.600'}
                            _hover={{ color: `${themeColor}.500` }} transition="color 0.2s">
                            {link.label}
                        </Box>
                    ))}
                    {useAccountMenu && (
                        <NavDropdown label="Account" links={accountLinks} linkHref={linkHref} themeColor={themeColor} emphasize />
                    )}
                    {navLinks.some((l) => l.isAdmin || l.isDriver) && (
                        <Menu>
                            <MenuButton as={Box} cursor="pointer" fontSize="sm" fontWeight="500"
                                color="gray.400" whiteSpace="nowrap" _hover={{ color: `${themeColor}.500` }}>
                                <HStack spacing={1}>
                                    <Text>Staff</Text>
                                    <Icon as={FiChevronDown} boxSize={3} />
                                </HStack>
                            </MenuButton>
                            <MenuList minW="150px">
                                {navLinks.filter((l) => l.isAdmin || l.isDriver).map((link) => (
                                    <MenuItem key={link.label} as="a" href={linkHref(link)} fontSize="sm">
                                        {link.label}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    )}
                </HStack>

                <Button as="a" href={`/${laundryId}`} size="sm" colorScheme={themeColor} borderRadius="full" px={5} flexShrink={0} display={{ base: 'none', xl: 'inline-flex' }}>
                    Schedule Pickup
                </Button>

                <IconButton display={{ base: 'flex', xl: 'none' }} icon={isOpen ? <FiX /> : <FiMenu />} onClick={onToggle} variant="ghost" aria-label="Menu" size="sm" />
            </Flex>

            <Collapse in={isOpen} animateOpacity>
                <VStack display={{ base: 'flex', xl: 'none' }} spacing={3} pb={4} px={4} align="stretch">
                    {navLinks.map((link) => (
                        <Box key={link.label} as="a" href={linkHref(link)} py={2} fontSize="md" fontWeight={link.isMembership ? '700' : '500'} color={(link.isAccount || link.isMembership || link.isCommercial) ? `${themeColor}.600` : (link.isAdmin || link.isDriver) ? 'gray.400' : 'gray.700'} _hover={{ color: `${themeColor}.500` }} onClick={onToggle}>
                            {link.label}
                        </Box>
                    ))}
                    <Button as="a" href={`/${laundryId}`} colorScheme={themeColor} borderRadius="full" size="md" onClick={onToggle}>
                        Schedule Pickup
                    </Button>
                </VStack>
            </Collapse>
        </Box>
    );
}
